import { Spinner } from '@change/ds-core-ui';

import { stripHtmlTags } from '@change-corgi/core/html';
import { useKnownAvailableParams } from '@change-corgi/core-react/router';

import { withPrefetchedData } from 'src/shared/prefetch';

import type { NewsAndGuidesDetailsPagePrefetchedDataProps } from 'src/app/pages/newsAndGuides/details/pageContext';
import {
	NewsAndGuidesDetailsPageContextProvider,
	prefetchPageContext,
} from 'src/app/pages/newsAndGuides/details/pageContext';
import { addSchemeToUrl } from 'src/app/shared/utils/assets';
import { isLoaded } from 'src/app/shared/utils/async';

import { Layout } from '../layout';

import { NewsAndGuidesDetailsErrorHandler } from './ErrorHandler';

export const NewsAndGuidesDetails = withPrefetchedData(
	function NewsAndGuidesDetails({ prefetchedData }: NewsAndGuidesDetailsPagePrefetchedDataProps) {
		const { countryCode, slug } = useKnownAvailableParams<{ countryCode: string; slug: string }>();

		return (
			<NewsAndGuidesDetailsPageContextProvider
				slug={slug}
				countryCode={countryCode}
				prefetchedData={prefetchedData}
				errorHandler={NewsAndGuidesDetailsErrorHandler}
				loadingFallback={<Spinner className="mx-auto my-64" />}
			>
				<Layout />
			</NewsAndGuidesDetailsPageContextProvider>
		);
	},
	{
		prefetchName: __MODULE_HASH__,
		// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
		prefetchData: async (context) => prefetchPageContext(context.params.countryCode!, context.params.slug!, context),
		pageTitle: ({ prefetchedData, fromTemplate }) => {
			if (!isLoaded(prefetchedData.pageData)) {
				return undefined;
			}
			return fromTemplate(prefetchedData.pageData.pageData.details.title);
		},
		pageMetadata: ({ prefetchedData }) => {
			if (!isLoaded(prefetchedData.pageData)) {
				return {};
			}
			const { title, excerpt, media } = prefetchedData.pageData.pageData.details;
			const description = stripHtmlTags(excerpt).trim();
			const photo = media?.sizes.medium;
			const imageUrl = photo ? addSchemeToUrl(photo.url) : undefined;

			return {
				ogType: 'website',
				twitterCard: 'summary' as const,
				ogTitle: title,
				ogDescription: description,
				...(imageUrl && photo
					? {
							ogImage: imageUrl,
							ogImageSecureUrl: imageUrl,
							ogImageWidth: photo.width,
							ogImageHeight: photo.height,
							twitterImage: imageUrl,
							twitterImageAlt: title,
						}
					: {}),
			};
		},
	},
);
